import React, { useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface EMSpectrumAbsorptionProps {
  width?: number;
  height?: number;
}

export const EMSpectrumAbsorption: React.FC<EMSpectrumAbsorptionProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const time = frameCount * 0.05;
    const padding = 20;
    const topY = 40;
    const stripHeight = 30;
    
    // Draw Spectrum Strip
    const spectrumGrad = ctx.createLinearGradient(padding, 0, size - padding, 0);
    spectrumGrad.addColorStop(0, '#64748B'); // Radio (Grey)
    spectrumGrad.addColorStop(0.2, '#991B1B'); // Microwave (Dark Red)
    spectrumGrad.addColorStop(0.35, '#EA580C'); // Infrared (Orange)
    
    // Visible (Rainbow)
    spectrumGrad.addColorStop(0.45, '#EF4444');
    spectrumGrad.addColorStop(0.5, '#EAB308');
    spectrumGrad.addColorStop(0.55, '#22C55E');
    spectrumGrad.addColorStop(0.6, '#3B82F6');
    
    spectrumGrad.addColorStop(0.7, '#9333EA'); // UV (Purple)
    spectrumGrad.addColorStop(0.85, '#E0F2FE'); // X-ray (Blue-white)
    spectrumGrad.addColorStop(1, '#FFFFFF'); // Gamma (White)
    
    ctx.fillStyle = spectrumGrad;
    ctx.fillRect(padding, topY, size - padding * 2, stripHeight);

    // Draw Atmosphere Curve
    const groundY = size - 40;
    const atmoY = size - 120;
    
    ctx.beginPath();
    ctx.moveTo(0, groundY);
    ctx.quadraticCurveTo(size / 2, atmoY, size, groundY);
    ctx.lineTo(size, size);
    ctx.lineTo(0, size);
    
    const atmoGrad = ctx.createLinearGradient(0, atmoY, 0, groundY);
    atmoGrad.addColorStop(0, 'rgba(56, 189, 248, 0.8)'); // Blue sky
    atmoGrad.addColorStop(1, '#1E293B'); // Ground
    
    ctx.fillStyle = atmoGrad;
    ctx.fill();

    // Arrows Data
    const bands = [
      { name: 'Radio', x: 0.1, status: 'pass', color: '#94A3B8' },
      { name: 'IR', x: 0.35, status: 'partial', color: '#FB923C' },
      { name: 'Visible', x: 0.55, status: 'pass', color: '#4ADE80' },
      { name: 'UV', x: 0.7, status: 'partial', color: '#C084FC' },
      { name: 'X-Ray', x: 0.85, status: 'block', color: '#E0F2FE' },
      { name: 'Gamma', x: 0.95, status: 'block', color: '#FFFFFF' },
    ];

    ctx.textAlign = 'center';
    
    bands.forEach(band => {
      const x = padding + band.x * (size - padding * 2);
      const startY = topY + stripHeight + 10;
      
      let endY = startY;
      let symbol = '';
      let symbolColor = '';
      
      // Calculate curve hit y
      // quadraticCurve equation approximation
      const t = x / size;
      // P = (1-t)^2*P0 + 2(1-t)t*P1 + t^2*P2
      // Actually simpler: just approximate
      const curveY = groundY - (groundY - atmoY) * (1 - Math.pow((x - size/2)/(size/2), 2));
      
      if (band.status === 'pass') {
        endY = curveY + 20;
        symbol = '✓';
        symbolColor = '#4ADE80';
      } else if (band.status === 'block') {
        endY = curveY - 20;
        symbol = '✗';
        symbolColor = '#EF4444';
      } else {
        endY = curveY - 5;
        symbol = '〰'; // partial
        symbolColor = '#FACC15';
      }
      
      // Animate arrow
      const arrowOffset = (time * 10) % 20;
      
      ctx.beginPath();
      ctx.strokeStyle = band.color;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.lineDashOffset = -arrowOffset;
      ctx.moveTo(x, startY);
      ctx.lineTo(x, endY);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Draw arrowhead
      ctx.beginPath();
      ctx.fillStyle = band.color;
      ctx.moveTo(x - 4, endY - 4);
      ctx.lineTo(x + 4, endY - 4);
      ctx.lineTo(x, endY + 4);
      ctx.fill();

      // Draw Symbol
      ctx.font = 'bold 16px sans-serif';
      ctx.fillStyle = symbolColor;
      ctx.fillText(symbol, x, endY + 20);
      
      // Draw Label
      ctx.font = '10px sans-serif';
      ctx.fillStyle = '#E2E8F0';
      ctx.fillText(band.name, x, topY - 10);
      
      if (band.status === 'block') {
         ctx.font = '9px sans-serif';
         ctx.fillStyle = '#EF4444';
         ctx.fillText('BLOCKED', x, endY - 10);
      }
    });

    // Annotation
    ctx.fillStyle = '#F8FAFC';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Space telescopes needed for X-rays and Gamma', size / 2, size - 15);

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};
